import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { GET_POLICIES_FOR_VALIDATION } from '@atom/graph/policy';
import { DUPLICATE_ROLE } from '@atom/graph/role';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  Checkbox,
  Icon,
  Modal,
  Progress,
  Snackbar,
  TextField,
} from '@atom/mui';
import { SubjectIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Policy, PolicyAction, Resource } from '@atom/types/policy';
import { Role, RoleDuplicateInput } from '@atom/types/role';
import {
  getPolicyActionLabel,
  getResourceOptions,
  PolicyResourceOption,
} from '@atom/utilities/policyUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import LoadMoreButton from '../loadMoreButton/LoadMoreButton';

import classes from './roleDuplicateModal.module.css';

const NAME_LIMIT = 100;
const DESCRIPTION_LIMIT = 200;
const HTTP_STATUS_CONFLICT = 409;
const POLICIES_LIMIT = 5;

interface Props {
  open: boolean;
  onClose: (refetch: boolean) => void;
  role: Role;
}

const styles = {
  modal: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '30.375rem',
  },
  input: {
    margin: '1rem 0 0 0',
  },
  icon: {
    marginRight: '2rem',
    marginTop: '2rem',
  },
  characterCount: {
    fontSize: '0.75rem',
    float: 'right',
  },
};

const getInitialState = (role: Role): RoleDuplicateInput => ({
  id: role?.id || '',
  name: `Copy of ${role?.name || ''}`,
  description: role?.description || '',
  policyIds: [],
});

const getLabelForGrants = (grants: Resource[]) => {
  if (!grants.length) {
    return 'All Sub Source';
  }

  return grants.map(grant => grant.name).join(', ');
};

const getLabelForAction = (action: PolicyAction) => {
  return action === PolicyAction.ALL
    ? 'All Actions'
    : `${getPolicyActionLabel(action)} Only`;
};

const getPolicyLabel = (
  policy: Policy,
  resourceOptions: { [key: string]: PolicyResourceOption },
): React.ReactNode => {
  const option = resourceOptions[policy.resource];

  if (!option) {
    return null;
  }

  const label = [
    option.label,
    getLabelForGrants(policy.grants),
    getLabelForAction(policy.action),
  ].join('; ');

  return (
    <div className={classes.policyRow}>
      <span style={{ marginRight: '1rem' }}>{option.icon}</span>
      <div>{label}</div>
    </div>
  );
};

const RoleDuplicateModal = ({ open, onClose, role }: Props) => {
  const [, { navigate }] = useNavigation();
  const preferences = usePreferences();

  const [input, setInput] = useState<RoleDuplicateInput>(getInitialState(role));
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [page, setPage] = useState<number>(1);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const { name, description, policyIds } = input;
  const resourceOptions = getResourceOptions(preferences);

  const resetState = () => {
    setInput(getInitialState(role));
    setPolicies([]);
    setPage(1);
    setAllSelected(false);
    setErrorText('');
  };

  useEffect(() => {
    resetState();
  }, [role]);

  const [
    getPolicies,
    { data, loading: loadingPolicies, error: policiesError },
  ] = useLazyQuery(GET_POLICIES_FOR_VALIDATION, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const getPolicyRequest = async () => {
      const { data: policyData } = await getPolicies({
        variables: {
          input: {
            page,
            sortBy: 'resource',
            sortDirection: 'asc',
            subjectIds: [role?.id],
            limit: POLICIES_LIMIT,
          },
        },
      });
      setPolicies(current => [...current, ...policyData.policies.policies]);
    };

    if (open) {
      getPolicyRequest();
    }
  }, [open, page]);

  const [duplicateRole, { loading }] = useMutation(DUPLICATE_ROLE);

  const isValid = () => {
    return Boolean(name && description && (allSelected || policyIds.length));
  };

  const togglePolicyId = (policyId: string) => {
    const isToggled = policyIds.includes(policyId);

    if (isToggled) {
      setInput(current => ({
        ...current,
        policyIds: current.policyIds.filter(id => id !== policyId),
      }));
    } else {
      setInput(current => ({
        ...current,
        policyIds: [...current.policyIds, policyId],
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const res = await duplicateRole({
        variables: {
          input: {
            id: role?.id,
            name,
            description,
            ...(!allSelected && {
              policyIds,
            }),
          },
        },
      });

      onClose(true);

      Snackbar.info({
        message: `Created role "${name}"`,
        action: 'View',
        onActionClick: () =>
          navigate(`/team/role/${res?.data?.roleDuplicate?.id}`),
      });
    } catch (err) {
      if (getErrorCode(err) === HTTP_STATUS_CONFLICT) {
        setErrorText('This name is already in use.');
      } else {
        Snackbar.error({ message: 'An unknown error occurred' });
      }
    }
  };

  const countStyle = (inputLength, maxLength) =>
    inputLength > maxLength ? { color: colors.brand.red } : {};

  const characterLimitReached =
    name.length > NAME_LIMIT || description.length > DESCRIPTION_LIMIT;

  const hasMore = data?.policies?.totalCount > policies.length;

  return (
    <Modal
      title="Duplicate Role"
      open={open}
      onCancel={() => onClose(false)}
      onConfirm={handleSubmit}
      loading={loading}
      onExited={resetState}
      disabled={!isValid() || characterLimitReached}
      confirmButtonText="Duplicate"
      contentStyle={styles.modal}
    >
      <div className={classes.modalRow}>
        <Icon style={styles.icon}>
          <img src={renameIcon} />
        </Icon>
        <div style={{ width: '100%' }}>
          <TextField
            name="name"
            label="Name"
            value={name}
            required
            onChange={event =>
              setInput(current => ({ ...current, name: event.target.value }))
            }
            error={!!errorText}
            helperText={errorText}
            style={styles.input}
          />
          <div style={styles.characterCount}>
            <span style={countStyle(name.length, NAME_LIMIT)}>
              {name.length}/{NAME_LIMIT}
            </span>
          </div>
        </div>
      </div>
      <div className={classes.modalRow}>
        <Icon style={styles.icon} icon={SubjectIcon} />
        <div style={{ width: '100%' }}>
          <TextField
            multiline
            required
            name="description"
            label="Description"
            value={description}
            onChange={event =>
              setInput(current => ({
                ...current,
                description: event.target.value,
              }))
            }
            style={styles.input}
          />
          <div style={styles.characterCount}>
            <span style={countStyle(description.length, DESCRIPTION_LIMIT)}>
              {description.length}/{DESCRIPTION_LIMIT}
            </span>
          </div>
        </div>
      </div>
      <>
        <div className={classes.policiesTitle}>Policies</div>
        {loadingPolicies && !policies.length ? (
          <Progress style={{ flex: 1 }} />
        ) : (
          <div>
            <div>
              <Checkbox
                disabled={!data?.policies?.totalCount}
                checked={allSelected}
                onChange={() => setAllSelected(!allSelected)}
                label={`All of ${data?.policies?.totalCount || 0} policies`}
              />
            </div>
            {policies.map(policy => {
              const option = resourceOptions[policy.resource];

              if (!option) {
                return null;
              }

              return (
                <div key={policy.id}>
                  <Checkbox
                    checked={policyIds.includes(policy.id) || allSelected}
                    disabled={allSelected}
                    onChange={() => togglePolicyId(policy.id)}
                    label={getPolicyLabel(policy, resourceOptions)}
                  />
                </div>
              );
            })}
          </div>
        )}
        {hasMore && (
          <LoadMoreButton
            loading={loadingPolicies}
            error={!!policiesError}
            onClick={() => setPage(current => current + 1)}
          />
        )}
      </>
    </Modal>
  );
};

export default RoleDuplicateModal;
